﻿import React, { useState, useEffect, useRef } from 'react'
import { usePermissions } from '../../hooks/usePermissions'
import { useUserLogs } from '../../hooks/useUserLogs'
import { useAuthContext } from '../../contexts/AuthContext'
import { Heading, Text, Button, Input, Select, LoadingSpinner, Tooltip } from '../../components/ui'
import { BRAND_COLOR } from '../../constants'
import { modulesService } from '../../services/modules'
import {
  listArticles,
  createArticle,
  updateArticle,
  deleteArticle,
  getMultipleUsersInfo,
  getAllUsers,
  getArticleVersions,
  restoreArticleVersion,
  getBaseConhecimentoStats,
} from '../../services/baseConhecimento'
import VersionDiff from '../../components/VersionDiff'
import { BaseConhecimentoArticle, CreateBaseConhecimentoPayload } from '../../types/baseConhecimento'
import {
  MagnifyingGlassIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  DocumentTextIcon,
  CalendarIcon,
  UserIcon,
  CheckIcon,
  XMarkIcon,
  EyeIcon,
  ClockIcon,
  FunnelIcon,
  AdjustmentsHorizontalIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  ArrowPathIcon,
  BarsArrowDownIcon,
  BarsArrowUpIcon,
} from '@heroicons/react/24/outline'

const MODULE_ROUTE = '/ia/base-conhecimento'
const MODULE_NAME_FALLBACK = 'ia_base_conhecimento'
const LOG_ORIGEM = 'ia/base-conhecimento'
const LOG_ENTITY_TYPE = 'ia_base_conhecimento' as const

function articleLogFields(row: {
  id?: string
  title: string
  body: string
  responsible_id?: string
  current_version?: number | null
}) {
  return {
    id: row.id ?? null,
    title: row.title,
    body: row.body,
    responsible_id: row.responsible_id ?? null,
    current_version: row.current_version ?? null,
  }
}

const BaseConhecimento: React.FC = () => {
  const { profile } = useAuthContext()
  const { isAdmin, loading: loadingPermissions } = usePermissions()
  const { logCreate, logUpdate, logDelete, logView } = useUserLogs()

  const [canShow, setCanShow] = useState(false)
  const [canInsert, setCanInsert] = useState(false)
  const [canUpdate, setCanUpdate] = useState(false)
  const [canDelete, setCanDelete] = useState(false)
  const [loadingAccess, setLoadingAccess] = useState(true)

  const [articles, setArticles] = useState<BaseConhecimentoArticle[]>([])
  const [authors, setAuthors] = useState<Array<{ id: string; name: string }>>([])
  const [responsibles, setResponsibles] = useState<Array<{ id: string; name: string }>>([])
  const [allUsers, setAllUsers] = useState<Array<{ id: string; name: string }>>([])
  const [userNames, setUserNames] = useState<Record<string, string>>({})
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [retryCount, setRetryCount] = useState(0)

  const [searchTerm, setSearchTerm] = useState('')
  const [selectedAuthor, setSelectedAuthor] = useState('')
  const [selectedResponsible, setSelectedResponsible] = useState('')
  const [sortBy, setSortBy] = useState('created_at')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc')
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(searchTerm)

  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(25)
  const [totalItems, setTotalItems] = useState(0)
  const [totalPages, setTotalPages] = useState(0)

  const [showModal, setShowModal] = useState(false)
  const [showViewModal, setShowViewModal] = useState(false)
  const [showVersionsModal, setShowVersionsModal] = useState(false)
  const [showVersionViewModal, setShowVersionViewModal] = useState(false)
  const [showVersionDiffModal, setShowVersionDiffModal] = useState(false)
  const [isViewMaximized, setIsViewMaximized] = useState(false)

  const [editingArticle, setEditingArticle] = useState<BaseConhecimentoArticle | null>(null)
  const [viewingArticle, setViewingArticle] = useState<BaseConhecimentoArticle | null>(null)
  const [versionsArticle, setVersionsArticle] = useState<BaseConhecimentoArticle | null>(null)

  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({})
  const [viewingVersion, setViewingVersion] = useState<any>(null)
  const [versions, setVersions] = useState<any[]>([])
  const [versionsLoading, setVersionsLoading] = useState(false)

  const [formData, setFormData] = useState<CreateBaseConhecimentoPayload>({
    title: '',
    body: '',
    responsible_id: ''
  })
  const [modalLoading, setModalLoading] = useState(false)

  const [stats, setStats] = useState<{ total_articles: number } | null>(null)
  const [filtersExpanded, setFiltersExpanded] = useState(false)

  useEffect(() => {
    const resolveAccess = async () => {
      try {
        setLoadingAccess(true)

        if (isAdmin) {
          setCanShow(true)
          setCanInsert(true)
          setCanUpdate(true)
          setCanDelete(true)
          return
        }

        if (!profile?.user_group_id) {
          setCanShow(false)
          setCanInsert(false)
          setCanUpdate(false)
          setCanDelete(false)
          return
        }

        const { data, success } = await modulesService.getActiveWithPermissionsByGroup(profile.user_group_id)
        if (!success || !data) {
          setCanShow(false)
          setCanInsert(false)
          setCanUpdate(false)
          setCanDelete(false)
          return
        }

        const mod = data.find(
          (m) =>
            m.route === MODULE_ROUTE ||
            m.name === MODULE_NAME_FALLBACK ||
            (m.display_name || '').toLowerCase().trim() === 'base de conhecimento'
        )

        setCanShow(!!mod?.can_show)
        setCanInsert(!!mod?.can_insert)
        setCanUpdate(!!mod?.can_update)
        setCanDelete(!!mod?.can_delete)
      } catch {
        setCanShow(false)
        setCanInsert(false)
        setCanUpdate(false)
        setCanDelete(false)
      } finally {
        setLoadingAccess(false)
      }
    }

    if (loadingPermissions) return
    void resolveAccess()
  }, [isAdmin, loadingPermissions, profile?.user_group_id])

  const fetchData = async (opts?: { showOverlay?: boolean; query?: string }) => {
    try {
      const showOverlay = opts?.showOverlay ?? true
      if (showOverlay) {
        setLoading(true)
      }
      setError(null)

      const [articlesData, statsData, allUsersData] = await Promise.all([
        listArticles({
          query: opts?.query ?? searchTerm,
          created_by: selectedAuthor || undefined,
          responsible_id: selectedResponsible || undefined,
          sort_by: sortBy,
          sort_order: sortOrder
        }, currentPage, itemsPerPage),
        getBaseConhecimentoStats(),
        getAllUsers()
      ])

      setArticles(articlesData.articles)
      setTotalItems(articlesData.total)
      setTotalPages(Math.ceil(articlesData.total / itemsPerPage))
      setStats(statsData)
      setAllUsers(allUsersData)

      const authorIds = Array.from(new Set(articlesData.articles.map(article => article.created_by).filter(Boolean))) as string[]
      const responsibleIds = Array.from(new Set(articlesData.articles.map(article => article.responsible_id).filter(Boolean))) as string[]
      const allUserIds = Array.from(new Set([...authorIds, ...responsibleIds]))

      if (allUserIds.length > 0) {
        const usersData = await getMultipleUsersInfo(allUserIds)
        const userNamesMap: Record<string, string> = {}
        const authorsList: Array<{ id: string; name: string }> = []
        const responsiblesList: Array<{ id: string; name: string }> = []

        Object.entries(usersData).forEach(([id, user]) => {
          userNamesMap[id] = user.name
          if (authorIds.includes(id)) {
            authorsList.push({ id, name: user.name })
          }
          if (responsibleIds.includes(id)) {
            responsiblesList.push({ id, name: user.name })
          }
        })
        setUserNames(userNamesMap)
        setAuthors(authorsList.sort((a, b) => a.name.localeCompare(b.name)))
        setResponsibles(responsiblesList.sort((a, b) => a.name.localeCompare(b.name)))
      }

      setRetryCount(0)
    } catch (err) {
      console.error('Erro ao carregar dados:', err)
      setError(err instanceof Error ? err.message : 'Erro desconhecido')
      setRetryCount(prev => prev + 1)
    } finally {
      setLoading(false)
    }
  }

  const retryFetch = () => {
    if (retryCount < 3) {
      fetchData()
    }
  }

  useEffect(() => {
    const handler = setTimeout(() => setDebouncedSearchTerm(searchTerm), 400)
    return () => clearTimeout(handler)
  }, [searchTerm])

  useEffect(() => {
    setCurrentPage(1)
  }, [debouncedSearchTerm, selectedAuthor, selectedResponsible, sortBy, sortOrder])

  const didMountRef = useRef(false)
  useEffect(() => {
    if (!didMountRef.current) {
      didMountRef.current = true
      fetchData({ showOverlay: true, query: debouncedSearchTerm })
      return
    }
    fetchData({ showOverlay: false, query: debouncedSearchTerm })
  }, [debouncedSearchTerm, currentPage, itemsPerPage, selectedAuthor, selectedResponsible, sortBy, sortOrder])

  const openModal = (article?: BaseConhecimentoArticle) => {
    if (article) {
      if (!canUpdate) {
        alert('Você não tem permissão para editar bases de conhecimento.')
        return
      }
    } else {
      if (!canInsert) {
        alert('Você não tem permissão para criar bases de conhecimento.')
        return
      }
    }

    if (article) {
      setEditingArticle(article)
      setFormData({
        title: article.title,
        body: article.body,
        responsible_id: article.responsible_id || ''
      })
    } else {
      setEditingArticle(null)
      setFormData({
        title: '',
        body: '',
        responsible_id: ''
      })
    }
    setShowModal(true)
  }

  const closeModal = () => {
    setShowModal(false)
    setEditingArticle(null)
    setValidationErrors({})
    setFormData({
      title: '',
      body: '',
      responsible_id: ''
    })
  }

  const openViewModal = (article: BaseConhecimentoArticle) => {
    setViewingArticle(article)
    setShowViewModal(true)
    setIsViewMaximized(false)
    void logView(LOG_ENTITY_TYPE, article.id, `Visualizou artigo: ${article.title}`)
  }

  const closeViewModal = () => {
    setShowViewModal(false)
    setViewingArticle(null)
    setIsViewMaximized(false)
  }

  const openVersionsModal = async (article: BaseConhecimentoArticle) => {
    setVersionsArticle(article)
    setShowVersionsModal(true)
    setVersionsLoading(true)

    try {
      const versionsData = await getArticleVersions(article.id)
      setVersions(versionsData)
    } catch (err) {
      console.error('Erro ao carregar versões:', err)
    } finally {
      setVersionsLoading(false)
    }
  }

  const closeVersionsModal = () => {
    setShowVersionsModal(false)
    setVersionsArticle(null)
    setVersions([])
  }

  const openVersionViewModal = (version: any) => {
    setViewingVersion(version)
    setShowVersionViewModal(true)
  }

  const closeVersionViewModal = () => {
    setShowVersionViewModal(false)
    setViewingVersion(null)
  }

  const openVersionDiffModal = () => {
    setShowVersionDiffModal(true)
  }

  const closeVersionDiffModal = () => {
    setShowVersionDiffModal(false)
  }

  const validateForm = () => {
    const errors: Record<string, string> = {}

    if (!formData.title || formData.title.trim() === '') {
      errors.title = 'Título é obrigatório'
    }

    if (!formData.responsible_id || formData.responsible_id === '') {
      errors.responsible_id = 'Responsável é obrigatório'
    }

    if (!formData.body || formData.body.trim() === '') {
      errors.body = 'Conteúdo é obrigatório'
    }

    setValidationErrors(errors)
    return Object.keys(errors).length === 0
  }

  const handleSave = async () => {
    if (!validateForm()) {
      return
    }

    try {
      setModalLoading(true)

      const payload: CreateBaseConhecimentoPayload = {
        title: formData.title.trim(),
        body: formData.body.trim(),
        responsible_id: formData.responsible_id
      }

      if (editingArticle) {
        const oldValues = articleLogFields(editingArticle)
        await updateArticle({ id: editingArticle.id, ...payload })
        await logUpdate(
          LOG_ENTITY_TYPE,
          editingArticle.id,
          `Atualizou artigo: ${payload.title}`,
          oldValues,
          articleLogFields({
            id: editingArticle.id,
            title: payload.title,
            body: payload.body,
            responsible_id: payload.responsible_id,
            current_version: editingArticle.current_version,
          }),
          { origem: LOG_ORIGEM }
        )
      } else {
        const id = await createArticle(payload)
        await logCreate(
          LOG_ENTITY_TYPE,
          id,
          `Criou artigo: ${payload.title}`,
          {
            origem: LOG_ORIGEM,
            new_values: articleLogFields({
              id,
              title: payload.title,
              body: payload.body,
              responsible_id: payload.responsible_id,
            }),
          }
        )
      }

      await fetchData()
      closeModal()
    } catch (err) {
      console.error('Erro ao salvar base de conhecimento:', err)
      const errorMessage = err instanceof Error ? err.message : 'Erro desconhecido ao salvar a base de conhecimento'
      alert(`Erro ao salvar base de conhecimento: ${errorMessage}`)
    } finally {
      setModalLoading(false)
    }
  }

  const handleDelete = async (id: string) => {
    if (!canDelete) {
      alert('Você não tem permissão para excluir bases de conhecimento.')
      return
    }

    if (window.confirm('Tem certeza que deseja excluir esta base de conhecimento?')) {
      try {
        const article = articles.find(a => a.id === id)
        await deleteArticle(id)
        if (article) {
          await logDelete(
            LOG_ENTITY_TYPE,
            id,
            `Excluiu artigo: ${article.title}`,
            {
              origem: LOG_ORIGEM,
              old_values: articleLogFields(article),
            }
          )
        }
        await fetchData()
      } catch (err) {
        console.error('Erro ao excluir base de conhecimento:', err)
      }
    }
  }

  const handleRestoreVersion = async (versionNumber: number) => {
    if (!canUpdate) {
      alert('Você não tem permissão para restaurar versões.')
      return
    }

    if (versionsArticle && window.confirm('Tem certeza que deseja restaurar esta versão?')) {
      try {
        const before = articleLogFields(versionsArticle)
        await restoreArticleVersion(versionsArticle.id, versionNumber)
        await logUpdate(
          LOG_ENTITY_TYPE,
          versionsArticle.id,
          `Restaurou versão ${versionNumber} do artigo: ${versionsArticle.title}`,
          before,
          { restored_from_version: versionNumber },
          { origem: LOG_ORIGEM }
        )
        await fetchData()
        closeVersionsModal()
      } catch (err) {
        console.error('Erro ao restaurar versão:', err)
      }
    }
  }

  const handleClearFilters = () => {
    setSearchTerm('')
    setDebouncedSearchTerm('')
    setSelectedAuthor('')
    setSelectedResponsible('')
    setSortBy('created_at')
    setSortOrder('desc')
    setCurrentPage(1)
  }

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= pagesCount) {
      setCurrentPage(page)
    }
  }

  const handleItemsPerPageChange = (newItemsPerPage: number) => {
    setItemsPerPage(newItemsPerPage)
    setCurrentPage(1)
  }

  const hasFilters = !!(debouncedSearchTerm || selectedAuthor || selectedResponsible)

  const resultsCount = totalItems
  const pagesCount = totalPages
  const paginatedArticles = articles

  const showingStart = resultsCount > 0 ? (currentPage - 1) * itemsPerPage + 1 : 0
  const showingEnd = Math.min(currentPage * itemsPerPage, resultsCount)

  useEffect(() => {
    if (pagesCount > 0 && currentPage > pagesCount) {
      setCurrentPage(1)
    }
  }, [pagesCount, currentPage])

  return (
    <>
      <div className="flex-1 flex flex-col overflow-hidden">
        {loading ? (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <LoadingSpinner size="lg" className="mx-auto mb-4" />
              <Text size="lg" className="text-gray-600 dark:text-gray-400 mb-2">
                Carregando Base de conhecimento...
              </Text>
              {retryCount > 0 && (
                <Text size="sm" className="text-gray-500 dark:text-gray-500">
                  Tentativa {retryCount} de 3...
                </Text>
              )}
            </div>
          </div>
        ) : error ? (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center max-w-md">
              <ExclamationTriangleIcon className="h-12 w-12 text-red-500 mx-auto mb-4" />
              <Text size="lg" className="text-red-600 dark:text-red-400 mb-2">
                Erro ao carregar Base de conhecimento
              </Text>
              <Text size="sm" className="text-gray-600 dark:text-gray-400 mb-4">
                {error}
              </Text>
              <Button
                onClick={retryFetch}
                className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 mx-auto"
              >
                <ArrowPathIcon className="h-4 w-4" />
                Tentar Novamente
              </Button>
              {retryCount > 0 && (
                <Text size="xs" className="text-gray-500 dark:text-gray-500 mt-2">
                  Já tentamos {retryCount} vezes
                </Text>
              )}
            </div>
          </div>
        ) : loadingPermissions || loadingAccess ? (
          <div className="flex-1 flex items-center justify-center">
            <LoadingSpinner size="lg" />
          </div>
        ) : !canShow ? (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <Text size="xl" className="text-gray-500 dark:text-gray-400 mb-2">
                Acesso Negado
              </Text>
              <Text size="sm" color="muted">
                Você não tem permissão para acessar a Base de conhecimento.
              </Text>
            </div>
          </div>
        ) : (
          <>
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between">
                <div>
                  <Heading as="h1" size="3xl" className="text-gray-900 dark:text-white">
                    Base de conhecimento
                  </Heading>
                  {stats && (
                    <Text size="sm" color="muted" className="mt-2">
                      {stats.total_articles} bases
                    </Text>
                  )}
                </div>
                <Button
                  onClick={() => openModal()}
                  className="flex items-center gap-2"
                  style={{ backgroundColor: BRAND_COLOR }}
                >
                  <PlusIcon className="h-5 w-5" />
                  Adicionar nova base de conhecimento
                </Button>
              </div>
            </div>

            <div className="p-6 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
              <div className="flex flex-wrap items-center justify-between gap-3 mb-0">
                <div className="flex flex-wrap items-center gap-2">
                  <Text size="sm" className="text-gray-600 dark:text-gray-400">
                    Ordenar por:
                  </Text>
                  <Select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    className="w-40"
                  >
                    <option value="created_at">Data</option>
                    <option value="title">Título</option>
                  </Select>
                  <Button
                    onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                    variant="outline"
                    size="sm"
                    className="flex items-center gap-1.5"
                    aria-label={sortOrder === 'asc' ? 'Ordem crescente' : 'Ordem decrescente'}
                    title={sortOrder === 'asc' ? 'Crescente' : 'Decrescente'}
                  >
                    {sortOrder === 'asc' ? (
                      <BarsArrowUpIcon className="h-4 w-4" />
                    ) : (
                      <BarsArrowDownIcon className="h-4 w-4" />
                    )}
                  </Button>
                  <Button
                    onClick={handleClearFilters}
                    variant="outline"
                    size="sm"
                    disabled={!hasFilters && sortBy === 'created_at' && sortOrder === 'desc'}
                    className="flex items-center gap-1.5"
                  >
                    <XCircleIcon className="h-4 w-4" />
                    Limpar filtros
                  </Button>
                </div>

                <Button
                  onClick={() => setFiltersExpanded(!filtersExpanded)}
                  variant="outline"
                  size="sm"
                  className="flex items-center gap-2"
                  aria-expanded={filtersExpanded}
                >
                  <FunnelIcon className="h-4 w-4" />
                  {filtersExpanded ? 'Ocultar Filtros' : 'Mostrar Filtros'}
                </Button>
              </div>

              <div
                className={`transition-all duration-300 ease-in-out ${
                  filtersExpanded
                    ? 'max-h-96 opacity-100 mt-4 overflow-visible'
                    : 'max-h-0 opacity-0 overflow-hidden pointer-events-none'
                }`}
              >
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 py-0.5">
                  <div className="md:col-span-2 min-w-0">
                    <Text size="sm" className="mb-2 text-gray-700 dark:text-gray-300 block">
                      Buscar
                    </Text>
                    <div className="relative">
                      <MagnifyingGlassIcon className="h-5 w-5 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                      <Input
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        placeholder="Buscar bases de conhecimento..."
                        className="pl-10 w-full"
                      />
                    </div>
                  </div>

                  <div className="min-w-0">
                    <Text size="sm" className="mb-2 text-gray-700 dark:text-gray-300 block">
                      Autor
                    </Text>
                    <Select
                      value={selectedAuthor}
                      onChange={(e) => setSelectedAuthor(e.target.value)}
                      className="w-full"
                    >
                      <option value="">Todos os autores</option>
                      {authors.map((author) => (
                        <option key={author.id} value={author.id}>
                          {author.name}
                        </option>
                      ))}
                    </Select>
                  </div>

                  <div className="min-w-0">
                    <Text size="sm" className="mb-2 text-gray-700 dark:text-gray-300 block">
                      Responsável
                    </Text>
                    <Select
                      value={selectedResponsible}
                      onChange={(e) => setSelectedResponsible(e.target.value)}
                      className="w-full"
                    >
                      <option value="">Todos os responsáveis</option>
                      {responsibles.map((responsible) => (
                        <option key={responsible.id} value={responsible.id}>
                          {responsible.name}
                        </option>
                      ))}
                    </Select>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-6">
              {paginatedArticles.length === 0 ? (
                <div className="text-center py-12">
                  <DocumentTextIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <Text size="lg" className="text-gray-500 mb-2">
                    Nenhuma base de conhecimento encontrada
                  </Text>
                  <Text size="sm" className="text-gray-400">
                    Tente ajustar os filtros ou adicionar uma nova base de conhecimento
                  </Text>
                </div>
              ) : (
                <div className="grid gap-4">
                  {paginatedArticles.map((article) => (
                    <div key={article.id} className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6 hover:shadow-md transition-shadow">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white">
                              {article.title}
                            </Heading>
                          </div>

                          <div className="flex items-center gap-4 text-sm text-gray-500 dark:text-gray-400 mb-3">
                            <div className="flex items-center gap-1">
                              <CalendarIcon className="h-4 w-4" />
                              {new Date(article.created_at).toLocaleDateString('pt-BR')}
                            </div>
                            <div className="flex items-center gap-1">
                              <UserIcon className="h-4 w-4" />
                              {userNames[article.created_by || ''] || article.created_by || 'Usuário'}
                            </div>
                            {article.responsible_id && (
                              <div className="flex items-center gap-1">
                                <UserIcon className="h-4 w-4" />
                                <span className="text-xs text-gray-500">Resp:</span>
                                {userNames[article.responsible_id] || article.responsible_id || 'Usuário'}
                              </div>
                            )}
                          </div>

                          <div className="text-gray-600 dark:text-gray-300 line-clamp-2 whitespace-pre-wrap">
                            {article.body.substring(0, 200)}{article.body.length > 200 ? '...' : ''}
                          </div>
                        </div>

                        <div className="flex items-center gap-2 ml-4">
                          <Tooltip content="Visualizar base de conhecimento">
                            <Button
                              onClick={() => openViewModal(article)}
                              size="sm"
                              variant="outline"
                              className="flex items-center gap-1"
                            >
                              <EyeIcon className="h-4 w-4" />
                            </Button>
                          </Tooltip>
                          <Tooltip content="Ver histórico de versões">
                            <Button
                              onClick={() => openVersionsModal(article)}
                              size="sm"
                              variant="outline"
                              className="flex items-center gap-1"
                            >
                              <ClockIcon className="h-4 w-4" />
                            </Button>
                          </Tooltip>
                          <Tooltip content="Editar base de conhecimento">
                            <Button
                              onClick={() => openModal(article)}
                              size="sm"
                              variant="outline"
                              className="flex items-center gap-1"
                            >
                              <PencilIcon className="h-4 w-4" />
                            </Button>
                          </Tooltip>
                          <Tooltip content="Excluir base de conhecimento">
                            <Button
                              onClick={() => handleDelete(article.id)}
                              size="sm"
                              variant="outline"
                              className="flex items-center gap-1 text-red-600 hover:text-red-700"
                            >
                              <TrashIcon className="h-4 w-4" />
                            </Button>
                          </Tooltip>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {!loading && resultsCount > 0 && (
              <div className="mt-6 w-full">
                <div className="flex flex-col sm:flex-row items-center justify-between gap-4 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
                  <div className="flex items-center gap-4">
                    <Text size="sm" className="text-gray-600 dark:text-gray-400">
                      {hasFilters ? (
                        <>Mostrando {showingStart} a {showingEnd} de {resultsCount} resultados filtrados</>
                      ) : (
                        <>Mostrando {showingStart} a {showingEnd} de {resultsCount} resultados</>
                      )}
                    </Text>
                    <Select
                      value={itemsPerPage.toString()}
                      onChange={(e) => handleItemsPerPageChange(parseInt(e.target.value))}
                      className="w-20"
                    >
                      <option value="10">10</option>
                      <option value="25">25</option>
                      <option value="50">50</option>
                      <option value="100">100</option>
                    </Select>
                    <Text size="sm" className="text-gray-600 dark:text-gray-400">
                      por página
                    </Text>
                  </div>

                  {pagesCount > 1 && (
                    <div className="flex items-center gap-2">
                      <Button
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                        size="sm"
                        variant="outline"
                      >
                        Anterior
                      </Button>

                      <div className="flex items-center gap-1">
                        {Array.from({ length: Math.min(5, pagesCount) }, (_, i) => {
                          const page = i + 1
                          return (
                            <Button
                              key={page}
                              onClick={() => handlePageChange(page)}
                              size="sm"
                              variant={currentPage === page ? 'solid' : 'outline'}
                              className={currentPage === page ? 'bg-primary-600 text-white' : ''}
                            >
                              {page}
                            </Button>
                          )
                        })}
                        {pagesCount > 5 && (
                          <>
                            <Text size="sm" className="text-gray-500">...</Text>
                            <Button
                              onClick={() => handlePageChange(pagesCount)}
                              size="sm"
                              variant={currentPage === pagesCount ? 'solid' : 'outline'}
                              className={currentPage === pagesCount ? 'bg-primary-600 text-white' : ''}
                            >
                              {pagesCount}
                            </Button>
                          </>
                        )}
                      </div>

                      <Button
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === pagesCount}
                        size="sm"
                        variant="outline"
                      >
                        Próxima
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
              <Heading as="h2" size="2xl" className="text-gray-900 dark:text-white">
                {editingArticle ? 'Editar base de conhecimento' : 'Adicionar nova base de conhecimento'}
              </Heading>
              <Button
                onClick={closeModal}
                variant="outline"
                size="sm"
                className="flex items-center gap-1"
              >
                <XMarkIcon className="h-4 w-4" />
                Fechar
              </Button>
            </div>

            <div className="p-6 overflow-y-auto flex-1">
              <div className="space-y-6">
                <div>
                  <Text size="sm" className="mb-2">
                    Título <span className="text-red-500">*</span>
                  </Text>
                  <Input
                    value={formData.title}
                    onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                    placeholder="Digite o título da base de conhecimento..."
                    className={validationErrors.title ? 'border-red-500' : ''}
                  />
                  {validationErrors.title && (
                    <Text size="sm" className="text-red-500 mt-1">
                      {validationErrors.title}
                    </Text>
                  )}
                </div>

                <div>
                  <Text size="sm" className="mb-2">
                    Responsável <span className="text-red-500">*</span>
                  </Text>
                  <Select
                    value={formData.responsible_id}
                    onChange={(e) => setFormData(prev => ({ ...prev, responsible_id: e.target.value }))}
                    className={validationErrors.responsible_id ? 'border-red-500' : ''}
                  >
                    <option value="">Selecione um responsável</option>
                    {allUsers.map(user => (
                      <option key={user.id} value={user.id}>
                        {user.name}
                      </option>
                    ))}
                  </Select>
                  {validationErrors.responsible_id && (
                    <Text size="sm" className="text-red-500 mt-1">
                      {validationErrors.responsible_id}
                    </Text>
                  )}
                </div>

                <div>
                  <Text size="sm" className="mb-2">
                    Conteúdo <span className="text-red-500">*</span>
                  </Text>
                  <textarea
                    value={formData.body}
                    onChange={(e) => setFormData(prev => ({ ...prev, body: e.target.value }))}
                    placeholder="Digite o conteúdo da base de conhecimento..."
                    disabled={modalLoading}
                    rows={12}
                    className={`w-full rounded-md border px-3 py-2 text-sm bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                      validationErrors.body
                        ? 'border-red-500'
                        : 'border-gray-300 dark:border-gray-600'
                    }`}
                  />
                  {validationErrors.body && (
                    <Text size="sm" className="text-red-500 mt-1">
                      {validationErrors.body}
                    </Text>
                  )}
                </div>
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 dark:border-gray-700">
              <Button
                onClick={closeModal}
                variant="outline"
                disabled={modalLoading}
              >
                Cancelar
              </Button>
              <Button
                onClick={handleSave}
                disabled={modalLoading || !formData.title.trim() || !formData.body.trim()}
                className="flex items-center gap-2"
                style={{ backgroundColor: BRAND_COLOR }}
              >
                {modalLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Salvando...
                  </>
                ) : (
                  <>
                    <CheckIcon className="h-4 w-4" />
                    {editingArticle ? 'Atualizar' : 'Criar'}
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}

      {showViewModal && viewingArticle && (
        <div className={`fixed inset-0 bg-black bg-opacity-50 flex ${isViewMaximized ? 'items-stretch justify-stretch p-0' : 'items-center justify-center p-4'} z-50`}>
          <div className={`bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full ${isViewMaximized ? 'max-w-none h-full' : 'max-w-4xl max-h-[90vh]'} flex flex-col`}>
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
              <Heading as="h2" size="2xl" className="text-gray-900 dark:text-white">
                {viewingArticle.title}
              </Heading>
              <div className="flex items-center gap-2">
                <Button
                  onClick={() => setIsViewMaximized(v => !v)}
                  size="sm"
                  variant="outline"
                  className="flex items-center gap-1"
                >
                  {isViewMaximized ? 'Restaurar' : 'Maximizar'}
                </Button>
                <Button
                  onClick={closeViewModal}
                  size="sm"
                  variant="outline"
                  className="flex items-center gap-1"
                >
                  <XMarkIcon className="h-4 w-4" />
                  Fechar
                </Button>
              </div>
            </div>

            <div className="p-6 overflow-y-auto flex-1">
              <div className="flex items-center gap-4 text-sm text-gray-500 dark:text-gray-400 flex-wrap mb-6">
                <div className="flex items-center gap-1">
                  <CalendarIcon className="h-4 w-4" />
                  {new Date(viewingArticle.created_at).toLocaleDateString('pt-BR')}
                </div>
                <div className="flex items-center gap-1">
                  <UserIcon className="h-4 w-4" />
                  {userNames[viewingArticle.created_by || ''] || viewingArticle.created_by || 'Usuário'}
                </div>
                {viewingArticle.responsible_id && (
                  <div className="flex items-center gap-1">
                    <UserIcon className="h-4 w-4" />
                    <span className="text-xs text-gray-500">Resp:</span>
                    {userNames[viewingArticle.responsible_id] || viewingArticle.responsible_id || 'Usuário'}
                  </div>
                )}
              </div>

              <div className="text-gray-800 dark:text-gray-200 whitespace-pre-wrap">
                {viewingArticle.body}
              </div>
            </div>
          </div>
        </div>
      )}

      {showVersionsModal && versionsArticle && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
              <Heading as="h2" size="2xl" className="text-gray-900 dark:text-white">
                Histórico: {versionsArticle.title}
              </Heading>
              <div className="flex items-center gap-2">
                {versions.length > 1 && (
                  <Button
                    onClick={openVersionDiffModal}
                    size="sm"
                    variant="outline"
                    className="flex items-center gap-1"
                  >
                    <AdjustmentsHorizontalIcon className="h-4 w-4" />
                    Comparar
                  </Button>
                )}
                <Button
                  onClick={closeVersionsModal}
                  size="sm"
                  variant="outline"
                  className="flex items-center gap-1"
                >
                  <XMarkIcon className="h-4 w-4" />
                  Fechar
                </Button>
              </div>
            </div>

            <div className="p-6 overflow-y-auto flex-1">
              {versionsLoading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
                  <Text className="ml-2">Carregando versões...</Text>
                </div>
              ) : versions.length === 0 ? (
                <div className="text-center py-8">
                  <ClockIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <Text className="text-gray-500">Nenhuma versão encontrada</Text>
                </div>
              ) : (
                <div className="space-y-4">
                  {versions.map((version) => (
                    <div key={version.id} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <span className="px-2 py-1 bg-primary-100 dark:bg-primary-900 text-primary-800 dark:text-primary-200 rounded-full text-xs font-medium">
                            v{version.version_number}
                          </span>
                          <Text size="sm" className="text-gray-500 dark:text-gray-400">
                            {new Date(version.created_at).toLocaleString('pt-BR')}
                          </Text>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            onClick={() => openVersionViewModal(version)}
                            size="sm"
                            variant="outline"
                            className="text-primary-600 hover:text-primary-700"
                          >
                            <EyeIcon className="h-4 w-4 mr-1" />
                            Visualizar
                          </Button>
                          <Button
                            onClick={() => handleRestoreVersion(version.version_number)}
                            size="sm"
                            variant="outline"
                            className="text-green-600 hover:text-green-700"
                          >
                            Restaurar
                          </Button>
                        </div>
                      </div>

                      <Text size="sm" className="text-gray-600 dark:text-gray-300 mb-2">
                        {version.change_summary || 'Sem descrição das mudanças'}
                      </Text>

                      <div className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                        Por: {version.user_name || 'Usuário'}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {showVersionViewModal && viewingVersion && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
              <Heading as="h2" size="2xl" className="text-gray-900 dark:text-white">
                Versão {viewingVersion.version_number}: {viewingVersion.title}
              </Heading>
              <Button
                onClick={closeVersionViewModal}
                size="sm"
                variant="outline"
                className="flex items-center gap-1"
              >
                <XMarkIcon className="h-4 w-4" />
                Fechar
              </Button>
            </div>

            <div className="p-6 overflow-y-auto flex-1">
              <div className="flex items-center gap-4 text-sm text-gray-500 dark:text-gray-400 flex-wrap mb-6">
                <div className="flex items-center gap-1">
                  <CalendarIcon className="h-4 w-4" />
                  {new Date(viewingVersion.created_at).toLocaleString('pt-BR')}
                </div>
                <div className="flex items-center gap-1">
                  <UserIcon className="h-4 w-4" />
                  {viewingVersion.user_name || 'Usuário'}
                </div>
                <span className="px-2 py-1 bg-primary-100 dark:bg-primary-900 text-primary-800 dark:text-primary-200 rounded-full text-xs">
                  v{viewingVersion.version_number}
                </span>
                {viewingVersion.change_summary && (
                  <div className="flex items-center gap-1">
                    <span className="text-gray-600 dark:text-gray-300">
                      {viewingVersion.change_summary}
                    </span>
                  </div>
                )}
              </div>

              <div className="text-gray-800 dark:text-gray-200 whitespace-pre-wrap">
                {viewingVersion.body}
              </div>
            </div>
          </div>
        </div>
      )}

      {showVersionDiffModal && versions.length > 0 && (
        <VersionDiff
          versions={versions}
          onClose={closeVersionDiffModal}
        />
      )}
    </>
  )
}

export default BaseConhecimento
